import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Typography, Space, Skeleton } from 'antd';
import {
  UserOutlined,
  ShoppingOutlined,
  ToolOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  RiseOutlined,
  CustomerServiceOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { formatNumber } from '../../utils/formatters';
import { dashboardService } from '../../services/dashboardService';
import { usersService } from '../../services/usersService';
import { serviceProduits } from '../../services/productsService';
import { interventionsService } from '../../services/interventionsService';
import orderService from '../../services/ordersService';
import { contractsService } from '../../services/contractsService';
import { complaintsService } from '../../services/complaintsService';

const { Text, Title } = Typography;

interface StatCardProps {
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color: string;
  trend?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend }) => (
  <Card
    style={{
      height: '100%',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      borderRadius: '8px'
    }}
    bodyStyle={{ padding: '16px' }}
  >
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <Space direction="vertical" size={4} style={{ flex: 1, minWidth: 0 }}>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {title}
          </Text>
          <Title level={4} style={{ margin: 0, fontWeight: 'bold', minHeight: '2rem', display: 'flex', alignItems: 'center', wordBreak: 'break-word' }}>
            {value}
          </Title>
          {trend && (
            <Space size={4}>
              <RiseOutlined style={{ color: trend.startsWith('+') ? '#52c41a' : '#ff4d4f', fontSize: 16 }} />
              <Text style={{ color: trend.startsWith('+') ? '#52c41a' : '#ff4d4f', fontSize: '12px' }}>
                {trend}
              </Text>
            </Space>
          )}
        </Space>
        <div
          style={{
            backgroundColor: color,
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          {React.isValidElement(icon) &&
            React.cloneElement(icon as React.ReactElement<any>, {
              style: { color: '#fff', fontSize: 24 }
            })}
        </div>
      </div>
    </Space>
  </Card>
);

const DashboardStats: React.FC = () => {
  // État pour les statistiques
  const [stats, setStats] = useState<Array<{
    title: string;
    value: string | React.ReactNode;
    icon: React.ReactNode;
    color: string;
    trend?: string;
    loading?: boolean;
    error?: boolean;
  }>>([
    {
      title: 'Utilisateurs',
      value: 0,
      icon: <UserOutlined />,
      color: '#1890ff',
      loading: true,
      error: false
    },
    {
      title: 'Produits',
      value: 0,
      icon: <ShoppingOutlined />,
      color: '#52c41a',
      loading: true,
      error: false
    },
    {
      title: 'Interventions',
      value: 0,
      icon: <ToolOutlined />,
      color: '#fa8c16',
      loading: true,
      error: false
    },
    {
      title: 'Commandes',
      value: 0,
      icon: <ShoppingCartOutlined />,
      color: '#722ed1',
      loading: true,
      error: false
    },
    {
      title: 'Contrats Actifs',
      value: 0,
      icon: <FileTextOutlined />,
      color: '#13c2c2',
      loading: true,
      error: false
    },
    {
      title: 'Réclamations',
      value: 0,
      icon: <CustomerServiceOutlined />,
      color: '#f5222d',
      loading: true,
      error: false
    }
  ]);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        console.log('Chargement des vraies statistiques du tableau de bord...');
        
        // Récupérer les données réelles depuis l'API
        const [
          usersData,
          productsData, 
          interventionsData,
          ordersData,
          contractsData,
          complaintsData
        ] = await Promise.allSettled([
          usersService.getUsers({ page: 1, limit: 1 }),
          serviceProduits.listerProduits({ page: 1, limit: 1 }),
          interventionsService.getInterventions(),
          orderService.getOrders(),
          contractsService.getContracts(),
          complaintsService.getComplaints()
        ]);

// Extraire les totaux des réponses — null = source indisponible (erreur API)
        const totalUsers = usersData.status === 'fulfilled' ? (usersData.value.data?.total ?? 0) : null;
        const totalProducts = productsData.status === 'fulfilled' ? (productsData.value.pagination?.total ?? 0) : null;
        const totalInterventions = interventionsData.status === 'fulfilled' ? (interventionsData.value.data?.total ?? 0) : null;
        const totalOrders = ordersData.status === 'fulfilled' ? (ordersData.value?.length ?? 0) : null;
        const activeContracts = contractsData.status === 'fulfilled' ? (contractsData.value?.data?.contracts?.length ?? 0) : null;
        const openComplaints = complaintsData.status === 'fulfilled' ? (complaintsData.value.data?.complaints?.length ?? 0) : null;

        console.log('Statistiques réelles chargées:', {
          totalUsers,
          totalProducts,
          totalInterventions,
          totalOrders,
          activeContracts,
          openComplaints
        });
        
        setStats([
          {
            title: 'Utilisateurs',
            value: totalUsers !== null ? formatNumber(totalUsers) : '—',
            icon: <UserOutlined />,
            color: '#1890ff',
            trend: totalUsers !== null && totalUsers > 0 ? `${totalUsers} au total` : undefined,
            loading: false,
            error: totalUsers === null
          },
          {
            title: 'Produits',
            value: totalProducts !== null ? formatNumber(totalProducts) : '—',
            icon: <ShoppingOutlined />,
            color: '#52c41a',
            loading: false,
            error: totalProducts === null
          },
          {
            title: 'Interventions',
            value: totalInterventions !== null ? formatNumber(totalInterventions) : '—',
            icon: <ToolOutlined />,
            color: '#fa8c16',
            loading: false,
            error: totalInterventions === null
          },
          {
            title: 'Commandes',
            value: totalOrders !== null ? formatNumber(totalOrders) : '—',
            icon: <ShoppingCartOutlined />,
            color: '#722ed1',
            loading: false,
            error: totalOrders === null
          },
          {
            title: 'Contrats Actifs',
            value: activeContracts !== null ? formatNumber(activeContracts) : '—',
            icon: <FileTextOutlined />,
            color: '#13c2c2',
            loading: false,
            error: activeContracts === null
          },
          {
            title: 'Réclamations',
            value: openComplaints !== null ? formatNumber(openComplaints) : '—',
            icon: <CustomerServiceOutlined />,
            color: '#f5222d',
            loading: false,
            error: openComplaints === null
          }
        ]);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Une erreur inconnue est survenue';
        console.error('Erreur lors du chargement des statistiques:', errorMessage);
        
        // Mettre à jour l'état d'erreur pour chaque carte
        setStats(prevStats => 
          prevStats.map(stat => ({
            ...stat,
            loading: false,
            error: true,
            value: (
              <Space size={4}>
                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                <Text type="danger" style={{ fontSize: '14px' }}>
                  Erreur
                </Text>
              </Space>
            )
          }))
        );
      }
    };

    fetchDashboardStats();
  }, []);

  return (
    <Row gutter={[12, 12]}>
      {stats.map((stat, index) => (
        <Col xs={24} sm={12} md={8} lg={6} xl={6} xxl={4} key={index}>
          <StatCard
            title={stat.title}
            value={
              stat.loading ? (
                <Skeleton.Input active size="small" style={{ width: 60 }} />
              ) : stat.error ? (
                <Space size={4}>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <Text type="danger">Erreur</Text>
                </Space>
              ) : (
                stat.value
              )
            }
            icon={stat.icon}
            color={stat.color}
            trend={stat.trend}
          />
        </Col>
      ))}
    </Row>
  );
};

export default DashboardStats;
